import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Hover-to-open behaviour shared by the desktop category controls (the side
// rail on the homepage, the icon row in the header elsewhere). Hovering an icon
// opens its menu; the menu stays open while the pointer is on the icons or the
// menu, and closes shortly after it leaves both. Clicking an icon goes to the
// category page. Escape closes.
export default function useCategoryHover() {
    const navigate = useNavigate();
    const [openTypeId, setOpenTypeId] = useState(null);
    const closeTimer = useRef(null);

    const cancelClose = useCallback(() => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    }, []);

    // Small grace period so moving from the icons into the menu doesn't close it.
    const scheduleClose = useCallback(() => {
        cancelClose();
        closeTimer.current = setTimeout(() => setOpenTypeId(null), 140);
    }, [cancelClose]);

    useEffect(() => cancelClose, [cancelClose]);

    useEffect(() => {
        if (!openTypeId) return undefined;
        const onKey = (e) => e.key === 'Escape' && setOpenTypeId(null);
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [openTypeId]);

    const close = useCallback(() => setOpenTypeId(null), []);

    // Props for each icon button.
    const iconProps = (t) => ({
        'aria-expanded': openTypeId === t.id,
        onMouseEnter: () => { cancelClose(); setOpenTypeId(t.id); },
        onFocus: () => setOpenTypeId(t.id),
        onClick: () => {
            if (t.route) {
                setOpenTypeId(null);
                navigate(t.route);
            } else {
                setOpenTypeId(openTypeId === t.id ? null : t.id);
            }
        },
    });

    // Props for the container around the icons and for the open menu.
    const hoverZoneProps = { onMouseEnter: cancelClose, onMouseLeave: scheduleClose };

    return { openTypeId, close, iconProps, hoverZoneProps };
}
